import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ENV } from '@app/constants/global.constants';
import { Env } from '@app/types/env';
import { AuthenticationService } from '@app/security/authentication.service';
import { DbProgressiveDownloadService } from './dbDownloadProgress.service';
import { ProgressSpinnerDialogService } from '@shared/services/progress-spinner-dialog.service';
import BlocTematicDTO from '@data/models/blocks/block-dto';
import { catchError, take } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, of, throwError } from 'rxjs';
import { ContentSortToolService } from './content-sort-tool.service';
import { JengaError } from '@app/types/error';

@Injectable({
  providedIn: 'root'
})
export class ThematicBlocsService {
  public thematicBlocks = new BehaviorSubject<BlocTematicDTO[]>([]);
  private readonly API_PATH = '/api';
  private readonly BLOCK_PATH = '/bloc-tematic';
  private readonly ORDEN_PATH = '/orden';
  private readonly SAVE_PATH = '/save';
  private readonly SEMDB_KEY = 'BLOCKS';

  constructor(
    @Inject(ENV) private env: Env,
    private http: HttpClient,
    private authService: AuthenticationService,
    private progressiveDownloadService: DbProgressiveDownloadService,
    private contentSortToolService: ContentSortToolService,
    private spinnerService: ProgressSpinnerDialogService
  ) {}

  public checkDB(): void {
    this.progressiveDownloadService.semDB[this.SEMDB_KEY].count().then(async (n: number) => {
      if (!n || n <= 0) {
        this.fetchBlocks();
      } else {
        const data = await this.progressiveDownloadService.semDB[this.SEMDB_KEY].toArray();
        this.thematicBlocks.next(
          this.contentSortToolService.sortBlocAcordingToOrderNumber(data.sort(this.contentSortToolService.sortBlocByNom))
        );
      }
    });
  }

  public fetchBlocks(): void {
    this.http
      .get<BlocTematicDTO[]>(`${this.env.apiBaseUrl}${this.API_PATH}${this.BLOCK_PATH}`)
      .pipe(take(1))
      .subscribe((data: BlocTematicDTO[]) => {
        if (data?.length) {
          this.progressiveDownloadService.semDB[this.SEMDB_KEY].clear().then(() => {});
          data.forEach((content) => {
            //Se guarda en IndexedDB
            this.progressiveDownloadService.semDB[this.SEMDB_KEY].put(content).then(() => {});
          });

          this.thematicBlocks.next(
            this.contentSortToolService.sortBlocAcordingToOrderNumber(data.sort(this.contentSortToolService.sortBlocByNom))
          );
        }
      });
  }

  public addBloc(bloc: BlocTematicDTO): Observable<BlocTematicDTO> {
    return this.http
      .post<BlocTematicDTO>(`${this.env.apiBaseUrl}${this.API_PATH}${this.BLOCK_PATH}${this.SAVE_PATH}`, bloc)
      .pipe(catchError((error) => throwError(error.error as JengaError)));
  }

  public editBloc(bloc: BlocTematicDTO): Observable<BlocTematicDTO> {
    return this.http
      .put<BlocTematicDTO>(`${this.env.apiBaseUrl}${this.API_PATH}${this.BLOCK_PATH}/${bloc.id}`, bloc)
      .pipe(catchError((error) => throwError(error.error as JengaError)));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteBloc(bloc: BlocTematicDTO): Observable<any> {
    // this.progressiveDownloadService.semDB[this.SEMDB_KEY].bulkDelete([bloc.id]).then(() => {});
    return this.http
      .delete(`${this.env.apiBaseUrl}${this.API_PATH}${this.BLOCK_PATH}/${bloc.id}`)
      .pipe(catchError((error) => throwError(error.error as JengaError)));
  }

  public changeOrder(blocks: BlocTematicDTO[]): Observable<BlocTematicDTO[]> {
    if (blocks.length) {
      return this.http
        .put<BlocTematicDTO[]>(
          `${this.env.apiBaseUrl}${this.API_PATH}${this.BLOCK_PATH}${this.ORDEN_PATH}`,
          blocks.map((b) => ({ id: b.id, ordre: b.ordre }))
        )
        .pipe(catchError((error) => throwError(error.error as JengaError)));
    }
    return of(null);
  }
}
